import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { observer } from 'mobx-react'

import {
  FeedbackText,
  Input,
  Label,
  PrimaryButton,
  RadioButton,
} from 'Components/_theme'
import { LegacyCountrySelect } from 'Components/LegacyCountrySelect/LegacyCountrySelect'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'

import { useStore } from 'Stores/index'
import { UserModeEnum } from 'Stores/portalSettingsStore/portalSettingsStore.types'

import { useActiveUser, useIsOrganisation } from 'Hooks'

import {
  getCityAndStateAbbr,
  getFullContactName,
  parseStreetString,
} from 'Utilities'

import { countryISOCodes } from 'Constants/constants'
import {
  E2E_NEW_ORDER_ADDRESS_FORM_CITY,
  E2E_NEW_ORDER_ADDRESS_FORM_FULL_NAME,
  E2E_NEW_ORDER_ADDRESS_FORM_NEXT,
  E2E_NEW_ORDER_ADDRESS_FORM_STREET_ADDITION,
  E2E_NEW_ORDER_ADDRESS_FORM_STREET_NAME,
  E2E_NEW_ORDER_ADDRESS_FORM_STREET_NUMBER,
  E2E_NEW_ORDER_ADDRESS_FORM_ZIP,
} from 'Constants/e2e'
import { usStatesList } from 'Constants/usStatesList'

import { OrderStickyFooter } from '../OrderStickyFooter/OrderStickyFooter'
import {
  AddressTypeEnum,
  OrderAddressFormProps,
} from './OrderAddressForm.types'

export const OrderAddressForm = observer((props: OrderAddressFormProps) => {
  const {
    chosenShippingAddress,
    chosenShippingAddressType,
    employeeContact,
    officeAddress,
    onSetChosenShippingAddressLabel,
    onSetChosenShippingAddressType,
    onBackButtonClick,
    onSubmitButtonClick,
  } = props

  const intl = useIntl()

  const { portalConfigStore, portalSettingsStore } = useStore()
  const { portalConfig } = portalConfigStore
  const { userMode } = portalSettingsStore
  const isOrganisation = useIsOrganisation()

  const { activeOrganisation, activeEmployee } = useActiveUser()

  const customDeliveryAddress = !!portalConfig?.customDeliveryAddress

  let shipDeviceToCustomAddress = !!portalConfig?.shipDeviceToCustomAddress

  if (
    userMode === UserModeEnum.NEW_JOINER &&
    portalConfig?.isCustomDeliveryAddressForNewJoinersDisabled
  ) {
    shipDeviceToCustomAddress = false
  }

  const shipToOfficeAddress =
    !!officeAddress && !!portalConfig?.shipToOfficeAddress

  const [to, setTo] = useState('')
  const [attn, setAttn] = useState('')
  const [streetName, setStreetName] = useState('')
  const [streetNumber, setStreetNumber] = useState('')
  const [streetAddition, setStreetAddition] = useState('')
  const [unitedStatesAddress, setUnitedStatesAddress] = useState('')
  const [zip, setZip] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [stateAbbr, setStateAbbr] = useState('')
  const [isValidated, setIsValidated] = useState(false)

  const generateOfficeName = useMemo(() => {
    if (officeAddress?.name) {
      return officeAddress.name
    }

    if (activeOrganisation?.shortName) {
      return `${activeOrganisation.shortName} Office`
    }

    if (activeEmployee?.organisation?.shortName) {
      return `${activeEmployee?.organisation?.shortName} Office`
    }

    return 'Office'
  }, [officeAddress, activeOrganisation, activeEmployee])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsValidated(true)

    if (event.currentTarget.checkValidity()) {
      onSubmitButtonClick({
        city:
          country === countryISOCodes.US && stateAbbr
            ? `${city}, ${stateAbbr}`
            : city,
        country,
        id: '',
        name: to,
        nameAddition: attn,
        street:
          country === countryISOCodes.US
            ? unitedStatesAddress
            : `${streetName} ${streetNumber}`,
        streetAddition,
        zipcode: zip,
      })
    }
  }

  const handleAddressTypeChange = useCallback(
    (addressType: AddressTypeEnum) => {
      if (addressType === AddressTypeEnum.Office) {
        const officeAddressStreet = parseStreetString(
          officeAddress?.street || '',
        )

        setTo(generateOfficeName)
        setAttn(
          (employeeContact || activeEmployee
            ? getFullContactName(employeeContact || activeEmployee?.contact)
            : officeAddress?.nameAddition) || '',
        )
        setStreetName(officeAddressStreet.streetName)
        setStreetNumber(officeAddressStreet.streetNumber)
        setStreetAddition(officeAddress?.streetAddition || '')
        setZip(officeAddress?.zipcode || '')
        setCity(officeAddress?.city || '')
        setCountry(officeAddress?.country || '')

        if (officeAddress?.country === countryISOCodes.US) {
          const cityAndStateAbbr = getCityAndStateAbbr(officeAddress?.city)

          setCity(cityAndStateAbbr.city)
          setStateAbbr(cityAndStateAbbr.stateAbbr)
          setUnitedStatesAddress(officeAddress?.street || '')
        }

        onSetChosenShippingAddressLabel(generateOfficeName)
      }

      if (addressType === AddressTypeEnum.Custom) {
        setTo(
          (employeeContact || activeEmployee
            ? getFullContactName(employeeContact || activeEmployee?.contact)
            : officeAddress?.nameAddition) || '',
        )
        setAttn('')
        setStreetName('')
        setStreetNumber('')
        setStreetAddition('')
        setZip('')
        setCity('')
        setCountry(officeAddress?.country || '')
        setUnitedStatesAddress('')

        onSetChosenShippingAddressLabel('Custom_address')
      }

      setIsValidated(false)
      onSetChosenShippingAddressType(addressType)
    },
    [
      activeEmployee,
      employeeContact,
      generateOfficeName,
      officeAddress,
      onSetChosenShippingAddressLabel,
      onSetChosenShippingAddressType,
    ],
  )

  useEffect(() => {
    if (chosenShippingAddress && chosenShippingAddressType !== null) {
      setTo(chosenShippingAddress.name || '')
      setAttn(chosenShippingAddress.nameAddition || '')
      setStreetName(parseStreetString(chosenShippingAddress?.street).streetName)
      setStreetNumber(
        parseStreetString(chosenShippingAddress?.street).streetNumber,
      )
      setStreetAddition(chosenShippingAddress.streetAddition || '')
      setZip(chosenShippingAddress.zipcode || '')
      setCity(chosenShippingAddress.city || '')
      setCountry(chosenShippingAddress.country || '')

      if (chosenShippingAddress.country === countryISOCodes.US) {
        const cityAndStateAbbr = getCityAndStateAbbr(chosenShippingAddress.city)

        setCity(cityAndStateAbbr.city)
        setStateAbbr(cityAndStateAbbr.stateAbbr)
        setUnitedStatesAddress(chosenShippingAddress.street || '')
      }

      return
    }

    if (shipToOfficeAddress) {
      handleAddressTypeChange(AddressTypeEnum.Office)

      return
    }

    if (
      !shipToOfficeAddress ||
      (customDeliveryAddress && shipDeviceToCustomAddress)
    ) {
      handleAddressTypeChange(AddressTypeEnum.Custom)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeContact, activeEmployee, activeOrganisation])

  if ((!officeAddress || !shipToOfficeAddress) && !shipDeviceToCustomAddress) {
    return <PageErrorState />
  }

  const isDisabled =
    chosenShippingAddressType === AddressTypeEnum.Office && !isOrganisation

  const shouldValidate = isValidated && !isDisabled

  const isUnitedStatesAddressType = country === countryISOCodes.US

  const renderUSStatesListCol = () => (
    <Col md={6} xs={12}>
      <Form.Group className="mb-6" controlId="stateAbbr">
        <Label htmlFor="stateAbbr" isRequired={isUnitedStatesAddressType}>
          <FormattedMessage id="NewOrderShipping_state_abbr" />
        </Label>

        <Form.Select
          disabled={isDisabled}
          name="stateAbbr"
          onChange={e => setStateAbbr(e.target.value)}
          required={isUnitedStatesAddressType}
          value={stateAbbr}
        >
          <option value="">
            {intl.formatMessage({
              id: 'NewOrderShipping_state_abbr_placeholder',
            })}
          </option>

          {usStatesList.map(usStateItem => (
            <option key={usStateItem.abbr} value={usStateItem.abbr}>
              {usStateItem.name}
            </option>
          ))}
        </Form.Select>

        {!stateAbbr && shouldValidate && (
          <FeedbackText isInvalid>
            <FormattedMessage id="NewOrderShipping_invalid" />
          </FeedbackText>
        )}
      </Form.Group>
    </Col>
  )

  const renderZipcodeCol = () => (
    <Col md={6} xs={12}>
      <Form.Group className="mb-6" controlId="zipcode">
        <Label isRequired>
          <FormattedMessage
            id={
              isUnitedStatesAddressType
                ? 'NewOrderShipping_zip'
                : 'NewOrderShipping_postcode'
            }
          />
        </Label>

        <Input
          data-e2e={E2E_NEW_ORDER_ADDRESS_FORM_ZIP}
          disabled={isDisabled}
          id="zipcode"
          isFilled={!!zip}
          isInvalid={!zip && shouldValidate}
          markAsRequired
          name="zipcode"
          onChange={e => setZip(e.target.value)}
          placeholder={intl.formatMessage({
            id: isUnitedStatesAddressType
              ? 'NewOrderShipping_enter_zip'
              : 'NewOrderShipping_postcode_placeholder',
          })}
          required
          type="text"
          value={zip}
        />

        {!zip && shouldValidate && (
          <FeedbackText isInvalid>
            <FormattedMessage id="NewOrderShipping_invalid" />
          </FeedbackText>
        )}
      </Form.Group>
    </Col>
  )

  const renderStreetAddition = () => (
    <Form.Group className="mb-6" controlId="streetAddition">
      <Label>
        <FormattedMessage id="NewOrderShipping_street_addition" />
      </Label>

      <Input
        data-e2e={E2E_NEW_ORDER_ADDRESS_FORM_STREET_ADDITION}
        disabled={isDisabled}
        id="streetAddition"
        isFilled={!!streetAddition}
        name="streetAddition"
        onChange={e => setStreetAddition(e.target.value)}
        placeholder={intl.formatMessage({
          id: 'NewOrderShipping_street_addition_placeholder',
        })}
        type="text"
        value={streetAddition}
      />
    </Form.Group>
  )

  return (
    <Form
      aria-label="form"
      id="custom-address-form"
      noValidate
      onSubmit={handleSubmit}
      validated={isValidated}
    >
      <Row className="pt-6">
        <Col>
          <Form.Group>
            {shipToOfficeAddress &&
              (shipDeviceToCustomAddress || isOrganisation) && (
                <Row key={officeAddress?.id} className="mb-6 mt-4">
                  <Col>
                    <RadioButton
                      checked={
                        chosenShippingAddressType === AddressTypeEnum.Office
                      }
                      id={officeAddress?.id}
                      label={generateOfficeName}
                      name="shipping-address"
                      onChange={() => {
                        handleAddressTypeChange(AddressTypeEnum.Office)
                      }}
                    />
                  </Col>
                </Row>
              )}

            {shipDeviceToCustomAddress &&
              (shipToOfficeAddress || isOrganisation) && (
                <Row className="mb-6">
                  <Col>
                    <RadioButton
                      checked={
                        chosenShippingAddressType === AddressTypeEnum.Custom
                      }
                      id="custom-address"
                      label={intl.formatMessage({
                        id: 'NewOrderShipping_custom_address',
                      })}
                      name="shipping-address"
                      onChange={() => {
                        handleAddressTypeChange(AddressTypeEnum.Custom)
                      }}
                    />
                  </Col>
                </Row>
              )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="py-6">
        <Col className="pe-4">
          <Row>
            <Col md={6} xs={12}>
              <Form.Group className="mb-6" controlId="fullName">
                <Label isRequired>
                  <FormattedMessage id="NewOrderShipping_to" />
                </Label>

                <Input
                  data-e2e={E2E_NEW_ORDER_ADDRESS_FORM_FULL_NAME}
                  disabled={isDisabled}
                  id="fullName"
                  isFilled={!!to}
                  isInvalid={!to && shouldValidate}
                  markAsRequired
                  name="fullName"
                  onChange={e => setTo(e.target.value)}
                  placeholder={intl.formatMessage({
                    id:
                      chosenShippingAddressType !== AddressTypeEnum.Custom
                        ? 'NewOrderShipping_enter_company'
                        : 'NewOrderShipping_enter_name',
                  })}
                  required
                  type="text"
                  value={to}
                />

                {!to && shouldValidate && (
                  <FeedbackText isInvalid>
                    <FormattedMessage id="NewOrderShipping_invalid" />
                  </FeedbackText>
                )}
              </Form.Group>
            </Col>

            <Col md={6} xs={12}>
              <Form.Group className="mb-6" controlId="attn">
                <Label
                  isRequired={
                    chosenShippingAddressType !== AddressTypeEnum.Custom
                  }
                >
                  <FormattedMessage id="NewOrderShipping_attn" />
                </Label>

                <Input
                  id="attn"
                  isFilled={!!attn}
                  isInvalid={
                    chosenShippingAddressType !== AddressTypeEnum.Custom &&
                    !attn &&
                    isValidated
                  }
                  markAsRequired={
                    chosenShippingAddressType !== AddressTypeEnum.Custom
                  }
                  name="attn"
                  onChange={e => setAttn(e.target.value)}
                  placeholder={intl.formatMessage({
                    id:
                      chosenShippingAddressType !== AddressTypeEnum.Custom
                        ? 'NewOrderShipping_enter_name'
                        : 'NewOrderShipping_enter_attn',
                  })}
                  required={
                    chosenShippingAddressType !== AddressTypeEnum.Custom
                  }
                  type="text"
                  value={attn}
                />

                {chosenShippingAddressType !== AddressTypeEnum.Custom &&
                  !attn &&
                  isValidated && (
                    <FeedbackText isInvalid>
                      <FormattedMessage id="NewOrderShipping_invalid" />
                    </FeedbackText>
                  )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {isUnitedStatesAddressType ? (
              <>
                <Col md={6} xs={12}>
                  <Form.Group className="mb-6" controlId="unitedStatesAddress">
                    <Label isRequired>
                      <FormattedMessage id="NewOrderShipping_street_name_and_number" />
                    </Label>

                    <Input
                      disabled={isDisabled}
                      id="unitedStatesAddress"
                      isFilled={!!unitedStatesAddress}
                      isInvalid={!unitedStatesAddress && shouldValidate}
                      markAsRequired
                      name="unitedStatesAddress"
                      onChange={e => setUnitedStatesAddress(e.target.value)}
                      placeholder={intl.formatMessage({
                        id: 'NewOrderShipping_street_name_and_number_placeholder',
                      })}
                      required
                      type="text"
                      value={unitedStatesAddress}
                    />

                    {!unitedStatesAddress && shouldValidate && (
                      <FeedbackText isInvalid>
                        <FormattedMessage id="NewOrderShipping_invalid" />
                      </FeedbackText>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} xs={12}>
                  {renderStreetAddition()}
                </Col>
              </>
            ) : (
              <>
                <Col md={6} xs={12}>
                  <Form.Group className="mb-6" controlId="streetName">
                    <Label isRequired>
                      <FormattedMessage id="NewOrderShipping_street_name" />
                    </Label>

                    <Input
                      data-e2e={E2E_NEW_ORDER_ADDRESS_FORM_STREET_NAME}
                      disabled={isDisabled}
                      id="streetName"
                      isFilled={!!streetName}
                      isInvalid={!streetName && shouldValidate}
                      markAsRequired
                      name="streetName"
                      onChange={e => setStreetName(e.target.value)}
                      placeholder={intl.formatMessage({
                        id: 'NewOrderShipping_street_name_placeholder',
                      })}
                      required
                      type="text"
                      value={streetName}
                    />

                    {!streetName && shouldValidate && (
                      <FeedbackText isInvalid>
                        <FormattedMessage id="NewOrderShipping_invalid" />
                      </FeedbackText>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6} xs={12}>
                  <Form.Group className="mb-6" controlId="streetNumber">
                    <Label isRequired>
                      <FormattedMessage id="NewOrderShipping_street_number" />
                    </Label>

                    <Input
                      data-e2e={E2E_NEW_ORDER_ADDRESS_FORM_STREET_NUMBER}
                      disabled={isDisabled}
                      id="streetNumber"
                      isFilled={!!streetNumber}
                      isInvalid={!streetNumber && shouldValidate}
                      markAsRequired
                      name="streetNumber"
                      onChange={e => setStreetNumber(e.target.value)}
                      placeholder={intl.formatMessage({
                        id: 'NewOrderShipping_street_number_placeholder',
                      })}
                      required
                      type="text"
                      value={streetNumber}
                    />

                    {!streetNumber && shouldValidate && (
                      <FeedbackText isInvalid>
                        <FormattedMessage id="NewOrderShipping_invalid" />
                      </FeedbackText>
                    )}
                  </Form.Group>
                </Col>

                <Col xs={12}>{renderStreetAddition()}</Col>
              </>
            )}
          </Row>

          <Row>
            <Col md={6} xs={12}>
              <Form.Group className="mb-6" controlId="city">
                <Label isRequired>
                  <FormattedMessage
                    id={
                      isUnitedStatesAddressType
                        ? 'NewOrderShipping_city'
                        : 'NewOrderShipping_town'
                    }
                  />
                </Label>

                <Input
                  data-e2e={E2E_NEW_ORDER_ADDRESS_FORM_CITY}
                  disabled={isDisabled}
                  id="city"
                  isFilled={!!city}
                  isInvalid={!city && shouldValidate}
                  markAsRequired
                  name="city"
                  onChange={e => setCity(e.target.value)}
                  placeholder={intl.formatMessage({
                    id: isUnitedStatesAddressType
                      ? 'NewOrderShipping_enter_city'
                      : 'NewOrderShipping_enter_town',
                  })}
                  required
                  type="text"
                  value={city}
                />

                {!city && shouldValidate && (
                  <FeedbackText isInvalid>
                    <FormattedMessage id="NewOrderShipping_invalid" />
                  </FeedbackText>
                )}
              </Form.Group>
            </Col>
            {isUnitedStatesAddressType
              ? renderUSStatesListCol()
              : renderZipcodeCol()}
          </Row>

          <Row>
            {isUnitedStatesAddressType && renderZipcodeCol()}
            <Col md={6} xs={12}>
              <LegacyCountrySelect
                controlId="country"
                disabled={isDisabled}
                handleOnChange={e => setCountry(e.target.value)}
                value={country}
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={6} xs={12}>
              <Form.Group>
                <span className="Label-required">* </span>
                <FormattedMessage id="NewOrderShipping_required" />
              </Form.Group>
            </Col>
          </Row>
        </Col>
      </Row>

      <OrderStickyFooter
        handlePreviousAction={onBackButtonClick}
        renderNextButtonElement={() => (
          <Col className="d-flex justify-content-end">
            <PrimaryButton
              className="ms-2"
              data-e2e={E2E_NEW_ORDER_ADDRESS_FORM_NEXT}
              id="next"
              type="submit"
            >
              <FormattedMessage id="Next" />
            </PrimaryButton>
          </Col>
        )}
      />
    </Form>
  )
})
